import {
    BANNER_V2_HEIGHT,
    BANNER_V2_MAX_WIDTH,
    BANNER_V3_DESKTOP_IMAGE_SIZES,
} from '@/components/banner-card/types';
import CardContent from '@/components/card/card-content';
import CardImage from '@/components/card/card-image';
import { BlurredBackgroundImage } from '@/components/ui/blurred-background-image';
import { useIsMobile } from '@/hooks/use-is-mobile';
import {
    DS_BREAKPOINT,
    // eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
    DS_COLOR,
} from '@calm/evexia-design-tokens';
import { CardContainer } from '@rhg/components/card';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import { Box, Theme, ThemeProvider, useTheme } from '@rhg/libs/material';
import {
    MaskOrientation,
    styleMaskedContainer,
    sxAppend,
} from '@rhg/libs/material/sx';
import { ForwardedRef, forwardRef, useMemo } from 'react';
import { BannerCardV3Props } from '../types';

const styleMaxColumnsContainer = { height: '100%' };

const styleActualGridWrapper = {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    position: 'relative',
    height: '100%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_COLOR.WHITE_100P
            : DS_COLOR.LIGHT_TEXT_BODY, // LIGHT_TEXT_BODY matches dark linear gradient color}
};
const styleContentWrapper = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    // Allow extra space on the left to not have text directly behind the nav arrows on smaller breakpoints
    pl: (theme: Theme) => theme.semantics.Spacing[4],
};
const styleContent = {
    justifySelf: 'center',
    position: 'relative',
    maxWidth: DS_BREAKPOINT.MEDIUM,
    width: '100%',
};
const styleCardContent = {
    zIndex: 2,
};

const styleBannerImage = {
    width: '100%',
    minHeight: '100%',
    maxHeight: BANNER_V2_HEIGHT,
};
const styleCardContainer = {
    width: '100%',
    maxWidth: BANNER_V2_MAX_WIDTH,
    height: '100%',
    '& .MuiPaper-root': { maxHeight: BANNER_V2_HEIGHT },
};
const styleMaskedImageContainer = {
    height: '100%',
    maxHeight: BANNER_V2_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
};

export const BannerCardV3Desktop = forwardRef<
    HTMLDivElement,
    BannerCardV3Props
>(function BannerCardV3Desktop(
    {
        title,
        subtitle,
        content,
        category,
        progress,
        button,
        image,
        container,
        sx,
    }: BannerCardV3Props,
    ref: ForwardedRef<HTMLDivElement>
) {
    const theme = useTheme();
    const lightTheme = useProvidedTheme('light');
    const darkTheme = useProvidedTheme('dark');
    const show1by1Ratio = useIsMobile('lg');

    const containerHandlers = useMemo(
        () => ({
            onMouseEnter: () => {},
            onMouseLeave: () => {},
            onFocus: () => {},
            onBlur: () => {},
            onClick: container?.onClick,
        }),
        [container?.onClick]
    );

    const entireCardButtonProps = {
        component: container?.component,
        href: container?.href,
        'data-cy': container?.['data-cy'],
        tabIndex: container?.isClickable ? 0 : -1,
        'aria-hidden': container?.isClickable ? undefined : true,
    };

    // TODO: switch this to be based on average image color
    // ref: https://codepen.io/AtomicForce/pen/nVgamy
    const isDarkImageGradient = theme.palette.mode === 'dark';

    return (
        <MaxColumnsContainer
            ref={ref}
            columns={12}
            sx={styleMaxColumnsContainer}
        >
            <CardContainer
                clickHandlers={containerHandlers}
                role={container?.role}
                label={container?.['aria-label']}
                ariaLabel={container?.['aria-label']}
                entireCardButtonProps={entireCardButtonProps}
                isEntireCardClickable={container?.isClickable}
                trackButton={container?.trackButton}
                trackProps={container?.trackProps}
                sx={sxAppend(styleCardContainer, sx)}
                data-cy="bannerCard"
                data-syntheticid={container?.['data-syntheticid']}
            >
                <BlurredBackgroundImage
                    orientation={MaskOrientation.Left}
                    src={image?.src}
                    sx={styleActualGridWrapper}
                >
                    <Box sx={sxAppend(styleContentWrapper, styleContent)}>
                        <ThemeProvider
                            theme={isDarkImageGradient ? darkTheme : lightTheme}
                        >
                            <CardContent
                                title={title.text}
                                titleComponent={title.component}
                                titleMaxLines={title.maxLines ?? 2}
                                titleBottomSpacing={title.bottomSpacing}
                                subtitle={subtitle?.text}
                                subtitleVariant={subtitle?.variant}
                                subtitleMaxLines={subtitle?.maxLines}
                                tag={content?.tag}
                                action={content?.action}
                                categoryTitleColor={category?.titleColor}
                                progress={progress?.value}
                                progressText={progress?.text}
                                progressTextAlt={progress?.textAlt}
                                sx={sxAppend(styleCardContent, content?.sx)}
                                additionalAction={button?.additionalAction}
                                buttonTopSpacing={button?.topSpacing}
                                buttonGroup={button?.group}
                                variant="bannerV2"
                                icon={title.icon}
                                titleTopSpacing={title.topSpacing}
                                categoryTitle={category?.title}
                                categoryDescription={category?.description}
                                categoryIcon={category?.icon}
                            />
                        </ThemeProvider>
                    </Box>
                    <Box
                        sx={sxAppend(
                            styleMaskedContainer(MaskOrientation.Right),
                            styleMaskedImageContainer
                        )}
                    >
                        <CardImage
                            imageSideRadiusEnabled={false}
                            src={image?.src}
                            ratio={show1by1Ratio ? '1:1' : '3:2'}
                            fillContainer
                            // Full-bleed image but only ~50% of the card: give it
                            // a real `sizes` so next/image stops fetching 100vw.
                            sizes={BANNER_V3_DESKTOP_IMAGE_SIZES}
                            priority={image?.props?.priority}
                            sx={styleBannerImage}
                            alt=""
                        />
                    </Box>
                </BlurredBackgroundImage>
            </CardContainer>
        </MaxColumnsContainer>
    );
});
